import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@breathelife/mui';
import { Participant, ParticipantMedicalInterviewStatus, ParticipantRoleName } from '@breathelife/types';
import { ImageTypes, Loader, ReactHtmlParser } from '@breathelife/ui-components';
import { ParticipantRole, ParticipantRoles } from '@zinnia/form-engine-sdk';

import { useAuraNextMedicalInterviewContext } from '../../../../../../Context/AuraNextMedicalInterviewContextProvider';
import { useAssistedApplicationContext } from '../../../../../../Hooks';
import styled from '../../../../../../Styles/themed-styled-components';
import { ParticipantRoleBadge } from '../../../../../Badge/ParticipantRoleBadge';
import { Icon } from '../../../../../Icons';
import Typography from '../../../../../Typography';
import { CustomSubmitButton } from './Styles';

const RoleBadgeWrapper = styled.div`
  margin-left: 8px;
`;

export const ParticipantContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.grey[10]};
  border: 1px solid ${(props) => props.theme.colors.grey[30]};
  border-radius: 4px;
  padding: 8px 8px 8px 24px;
  min-height: 60px;
`;

const participantMedicalInterviewStatusButtonToTranslationKey: Record<ParticipantMedicalInterviewStatus, string> = {
  NEW: 'notReadyYet',
  READY: 'start',
  STARTED: 'continue',
  COMPLETED: 'edit',
  NIGO: 'continue',
  SKIPPED: 'teleInterviewSelected',
};

const participantMedicalInterviewStatusToTranslationKey: Record<ParticipantMedicalInterviewStatus, string> = {
  NEW: 'notReadyYet',
  READY: 'start',
  STARTED: 'continue',
  COMPLETED: 'completed',
  NIGO: 'continue',
  SKIPPED: 'teleInterviewSelected',
};

const STATUSES_WITH_BUTTON: ParticipantMedicalInterviewStatus[] = ['READY', 'NIGO', 'STARTED', 'COMPLETED'];

type AuraNextParticipantProps = {
  participant: Participant;
  onFormParticipantIdChange: (participantId: string) => void;
};

const DBParticipantRoleToSDKParticipantRoleMap: Record<ParticipantRoleName, ParticipantRole> = {
  OWNER: ParticipantRoles.OWNER_PERSON,
  PRIMARY_OWNER: ParticipantRoles.OWNER_PERSON,
  SECONDARY_OWNER: ParticipantRoles.OWNER_PERSON,
  INSURED: ParticipantRoles.INSURED,
  LEGAL_GUARDIAN: ParticipantRoles.LEGAL_GUARDIAN,
  PAYER: ParticipantRoles.PAYER,
  BENEFICIARY: ParticipantRoles.PRIMARY_BENEFICIARY,
  DEPENDENT_CHILD: ParticipantRoles.INSURED,
};

export function AuraNextParticipant({
  participant,
  onFormParticipantIdChange,
}: AuraNextParticipantProps): ReactElement | null {
  const { t } = useTranslation();
  const { startInterview, isEnabled, isLoaded } = useAuraNextMedicalInterviewContext();
  const { areAllFieldsDisabled } = useAssistedApplicationContext();

  if (!isLoaded) {
    return <Loader />;
  }

  if (!isEnabled || !participant.medicalInterview) {
    return null;
  }
  const { firstName = '', lastName = '' } = participant.data || {};
  const participantName = `${firstName} ${lastName}`;

  return (
    <ParticipantContainer>
      <Box display={'flex'} alignItems={'center'}>
        <Typography variant='h4'>
          <ReactHtmlParser html={participantName + ':'} />
        </Typography>
        {participant.roles.map((role) => (
          <RoleBadgeWrapper key={role}>
            <ParticipantRoleBadge role={DBParticipantRoleToSDKParticipantRoleMap[role]} />
          </RoleBadgeWrapper>
        ))}
      </Box>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {participant.medicalInterview.status &&
          (participant.medicalInterview.status === 'COMPLETED' ||
            participant.medicalInterview.status === 'NEW' ||
            participant.medicalInterview.status === 'SKIPPED') && (
            <Box display='flex' justifyContent='flex-end' alignItems='center' mr={1}>
              <Typography variant='h4'>
                {t(
                  `assistedApplication.auraNextMedicalQuestionnaireSubsection.statuses.${
                    participantMedicalInterviewStatusToTranslationKey[participant.medicalInterview.status]
                  }`,
                )}
              </Typography>

              {participant.medicalInterview.status === 'COMPLETED' && (
                <Box ml={1} display='flex' justifyContent='center' alignItems='center'>
                  <Icon
                    width='15px'
                    height='15px'
                    name='circledCheckMark'
                    variant='green'
                    inverted
                    stroke='none'
                    imageType={ImageTypes.meaningful}
                    ariaLabel={t('assistedApplication.sectionComplete')}
                  />
                </Box>
              )}
            </Box>
          )}
        {participant.medicalInterview.status &&
          STATUSES_WITH_BUTTON.includes(participant.medicalInterview.status) &&
          !areAllFieldsDisabled && (
            <CustomSubmitButton
              onClick={() => {
                onFormParticipantIdChange(participant.id);
                startInterview(participant.id);
              }}
            >
              {t(
                `assistedApplication.auraNextMedicalQuestionnaireSubsection.statuses.${
                  participantMedicalInterviewStatusButtonToTranslationKey[participant.medicalInterview.status]
                }`,
              )}
            </CustomSubmitButton>
          )}
      </div>
    </ParticipantContainer>
  );
}
